// Meta Pixel + Conversions API dual-tracking.
//
// Fetches the Pixel ID from the backend on first use, injects the fbevents
// script exactly once, and provides `track(event, custom, user)` which fires
// BOTH the browser Pixel and the server-side CAPI mirror using a shared
// event_id so Meta deduplicates them.
//
// Zero-config: if no Pixel ID is set in the admin, this is a silent no-op.

import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { trackingAllowed, piiAllowed, getConsent, onConsentChange } from "@/lib/consent";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = BASE ? `${BASE.replace(/\/$/, "")}/api` : "/api";

const STANDARD_EVENTS = new Set([
  "PageView",
  "ViewContent",
  "AddToCart",
  "InitiateCheckout",
  "Purchase",
  "Search",
  "AddToWishlist",
  "CompleteRegistration",
  "Lead",
]);

let _pixelId = null;
let _initPromise = null;
let _initialised = false;

function injectPixel(id) {
  if (typeof window === "undefined") return;
  if (window.fbq) {
    if (!_initialised) {
      window.fbq("init", id);
      _initialised = true;
    }
    return;
  }
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", id);
  _initialised = true;
}

async function ensureInitialised() {
  if (!trackingAllowed()) return null; // Dormant only if the visitor opted out
  if (_initialised) return _pixelId;
  if (_initPromise) return _initPromise;
  _initPromise = axios
    .get(`${API}/tracking/config`)
    .then((r) => {
      const id = r?.data?.pixel_id;
      if (id) {
        _pixelId = id;
        injectPixel(id);
      }
      return _pixelId;
    })
    .catch(() => null);
  return _initPromise;
}

// The browser Pixel's first-party cookies (_fbp / _fbc) live on whichever
// domain the shopper is on (luxsova.in or luxsova.com). Because the API
// always answers from luxsova.com, the server can't read the .in cookies on
// its own - so we pass them along explicitly with every server-side event.
function readCookie(name) {
  if (typeof document === "undefined") return undefined;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : undefined;
}

function fbIdentifiers() {
  const out = {};
  const fbp = readCookie("_fbp");
  let fbc = readCookie("_fbc");
  if (!fbc && typeof window !== "undefined") {
    const clid = new URLSearchParams(window.location.search).get("fbclid");
    if (clid) fbc = `fb.1.${Date.now()}.${clid}`;
  }
  if (fbp) out.fbp = fbp;
  if (fbc) out.fbc = fbc;
  return out;
}

/** Fire a Meta event on both the browser Pixel AND the server CAPI. */
// Context the checkout sends to the server so it can fire the Purchase itself.
export function getTrackingContext() {
  return {
    consent: getConsent() || "default",
    ...fbIdentifiers(),
    url: typeof window !== "undefined" ? window.location.href : undefined,
  };
}

export async function trackMeta(eventName, customData = {}, userData = {}, opts = {}) {
  try {
    if (!trackingAllowed()) return; // Respect visitor's opt-out choice
    const id = await ensureInitialised();
    if (!id) return; // Not configured
    const eventId = opts.eventId || uuidv4();

    // 1. Browser Pixel
    if (typeof window !== "undefined" && window.fbq) {
      const method = STANDARD_EVENTS.has(eventName) ? "track" : "trackCustom";
      window.fbq(method, eventName, customData, { eventID: eventId });
    }

    // 2. Server-side CAPI mirror (fire-and-forget). Skipped when the server
    //    sends this event itself (e.g. Purchase from /checkout/create-order).
    if (opts.mirror === false) return;
    axios
      .post(`${API}/tracking/events`, {
        event_name: eventName,
        event_id: eventId,
        custom_data: customData,
        // Personal details only after the visitor actively accepted.
        user_data: { ...fbIdentifiers(), ...(piiAllowed() ? userData : {}) },
        event_source_url:
          typeof window !== "undefined" ? window.location.href : undefined,
      })
      .catch(() => {
        /* silent */
      });
  } catch {
    /* never break the app on a tracking error */
  }
}

/** Convenience wrappers that mirror the events PixelYourSite Pro fires
 *  by default so behavior matches the WordPress site's Ads Manager. */
export const metaPageView = () => trackMeta("PageView");

export const metaViewContent = (product, variation) =>
  trackMeta("ViewContent", {
    content_ids: [String(product.id || product.slug)],
    content_type: "product",
    content_name: product.name,
    content_category:
      product.categories?.[0]?.name ||
      product.categories?.[0]?.slug ||
      undefined,
    value: Number(variation?.price || product.sale_price || product.price || 0),
    currency: "INR",
  });

export const metaAddToCart = (item) =>
  trackMeta("AddToCart", {
    content_ids: [String(item.id || item.slug)],
    content_type: "product",
    content_name: item.name,
    value: Number(item.price || 0) * Number(item.quantity || 1),
    currency: "INR",
    contents: [
      {
        id: String(item.id || item.slug),
        quantity: Number(item.quantity || 1),
        item_price: Number(item.price || 0),
      },
    ],
  });

export const metaInitiateCheckout = (items, total, form = {}) =>
  trackMeta(
    "InitiateCheckout",
    {
      content_ids: (items || []).map((i) => String(i.id || i.slug)),
      content_type: "product",
      num_items: (items || []).reduce((s, i) => s + Number(i.quantity || 1), 0),
      value: Number(total || 0),
      currency: "INR",
      contents: (items || []).map((i) => ({
        id: String(i.id || i.slug),
        quantity: Number(i.quantity || 1),
        item_price: Number(i.price || 0),
      })),
    },
    {
      email: form.email || undefined,
      phone: form.phone || undefined,
      first_name: form.name?.split(" ")?.[0] || undefined,
      last_name: form.name?.split(" ")?.slice(1)?.join(" ") || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      zip: form.pincode || undefined,
      country: form.country || "IN",
    },
  );

export const metaPurchase = (order, items, { serverSends = false } = {}) =>
  trackMeta(
    "Purchase",
    {
      content_ids: (items || []).map((i) => String(i.id || i.slug)),
      content_type: "product",
      num_items: (items || []).reduce((s, i) => s + Number(i.quantity || 1), 0),
      value: Number(order?.total || 0),
      currency: "INR",
      order_id: order?.order_number,
      contents: (items || []).map((i) => ({
        id: String(i.id || i.slug),
        quantity: Number(i.quantity || 1),
        item_price: Number(i.price || 0),
      })),
    },
    {
      email: order?.shipping?.email || undefined,
      phone: order?.shipping?.phone || undefined,
      first_name: order?.shipping?.name?.split(" ")?.[0] || undefined,
      last_name: order?.shipping?.name?.split(" ")?.slice(1)?.join(" ") || undefined,
      city: order?.shipping?.city || undefined,
      state: order?.shipping?.state || undefined,
      zip: order?.shipping?.pincode || undefined,
      country: order?.shipping?.country || "IN",
      external_id: order?.order_number,
    },
    { eventId: `purchase-${order?.order_number}`, mirror: !serverSends },
  );

// Product-page "Order via WhatsApp" - a high-intent lead (no cart/checkout).
export const metaLead = (product, price) =>
  trackMeta("Lead", {
    content_ids: [String(product.id || product.slug)],
    content_type: "product",
    content_name: product.name,
    value: Number(price || product.sale_price || product.price || 0),
    currency: "INR",
  });

export const metaSearch = (query) =>
  trackMeta("Search", { search_string: query });

export const metaAddToWishlist = (product) =>
  trackMeta("AddToWishlist", {
    content_ids: [String(product.id || product.slug)],
    content_type: "product",
    content_name: product.name,
    value: Number(product.sale_price || product.price || 0),
    currency: "INR",
  });

// Initialise as soon as the module is imported so PageView events aren't
// racing with the first fetch — but only after the visitor grants
// marketing consent. If they accept later, the banner emits
// `lmr:consent-change` and we retry the init then.
if (typeof window !== "undefined") {
  ensureInitialised();
  onConsentChange((v) => {
    if (v === "rejected" && window.fbq) {
      try { window.fbq("consent", "revoke"); } catch { /* ignore */ }
    }
    if (v === "accepted") {
      ensureInitialised().then((id) => {
        if (id && window.fbq) window.fbq("track", "PageView");
      });
    }
  });
}
